import React, { useEffect, Fragment } from 'react';
import { createPortal } from 'react-dom';
import { XIcon } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  children: React.ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl' | '2xl';
  backdropBlur?: boolean;
  stacked?: boolean;
}
const sizeStyles = {
  sm: 'max-w-md',
  md: 'max-w-lg',
  lg: 'max-w-2xl',
  xl: 'max-w-4xl',
  '2xl': 'max-w-6xl'
};
let modalCount = 0;
export function Modal({
  isOpen,
  onClose,
  title,
  children,
  size = 'md',
  backdropBlur = false,
  stacked = false
}: ModalProps) {
  useEffect(() => {
    if (isOpen) {
      modalCount++;
      const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
      document.body.style.overflow = 'hidden';
      document.body.style.paddingRight = `${scrollbarWidth}px`;
      return () => {
        modalCount--;
        if (modalCount === 0) {
          document.body.style.overflow = '';
          document.body.style.paddingRight = '';
        }
      };
    }
  }, [isOpen]);
  if (!isOpen) return null;
  const backdropZ = stacked ? 'z-[10000]' : 'z-[9990]';
  const modalZ = stacked ? 'z-[10010]' : 'z-[9999]';
  return createPortal(<Fragment data-id="element-252" data-mp="287w29kx6jty5">
      {/* Backdrop */}
      <div className={`fixed inset-0 bg-black/40 ${backdropZ} transition-opacity ${backdropBlur ? 'backdrop-blur-[6px]' : ''}`} onClick={onClose} aria-hidden="true" data-id="element-253" data-mp="2d3tcralri630" />
      

      {/* Desktop Modal */}
      <div className={`fixed inset-0 ${modalZ} overflow-y-auto hidden lg:block`} data-id="element-254" data-mp="244zms32xsdyf">
        
        <div className="flex min-h-full items-center justify-center p-4" data-id="element-255" data-mp="1pftfdrty3hfg">
          <div className={`
              relative bg-white rounded-xl shadow-xl w-full
              transform transition-all
              ${sizeStyles[size]}
            `} role="dialog" aria-modal="true" aria-labelledby={title ? 'modal-title' : undefined} data-id="element-256" data-mp="1udv9d7swt15p">
            
            {title && <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200" data-id="element-257" data-mp="1khrvj4op3e2x">
                <h2 id="modal-title" className="text-lg font-semibold text-gray-900" data-id="element-258" data-mp="3ibw08ix0wmpa">
                
                  {title}
                </h2>
                <button onClick={onClose} className="p-1 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors" aria-label="Cerrar" data-id="element-259" data-mp="3ddkqs9an4e7l">
                
                  <XIcon className="w-5 h-5" data-id="element-260" data-mp="9wcavuzh3ofi" />
                </button>
              </div>}
            <div className={title ? '' : 'pt-4'} data-id="element-261" data-mp="4whjzrfp6yug">{children}</div>
          </div>
        </div>
      </div>

      {/* Mobile Modal (bottom sheet) */}
      <div className={`fixed inset-0 ${modalZ} lg:hidden flex items-end`} onClick={e => {
      if (e.target === e.currentTarget) onClose();
    }} data-id="element-262" data-mp="3w1prt8zwxl2p">
        
        <div className="relative bg-white rounded-t-2xl shadow-2xl w-full max-h-[92vh] flex flex-col animate-slide-up" role="dialog" aria-modal="true" aria-labelledby={title ? 'modal-title-mobile' : undefined} style={{
        paddingBottom: 'env(safe-area-inset-bottom, 0px)'
      }} data-id="element-263" data-mp="2konb8dxtcvij">
          
          {/* Drag handle */}
          <div className="flex justify-center pt-3 pb-1 flex-shrink-0" data-id="element-264" data-mp="106w5ppuxr7p2">
            <div className="w-10 h-1 rounded-full bg-slate-300" data-id="element-265" data-mp="154xqcf80ff2n" />
          </div>

          {title && <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200 flex-shrink-0" data-id="element-266" data-mp="19wftn9xi9rqs">
              <h2 id="modal-title-mobile" className="text-base font-bold text-gray-900" data-id="element-267" data-mp="1etxro59zimk0">
              
                {title}
              </h2>
              <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600 rounded-xl hover:bg-gray-100 transition-colors -mr-1" aria-label="Cerrar" data-id="element-268" data-mp="1jw6l12qrrqmn">
              
                <XIcon className="w-5 h-5" data-id="element-269" data-mp="1su8mfjef0lag" />
              </button>
            </div>}
          <div className={`flex-1 overflow-y-auto ${title ? '' : 'pt-2'}`} data-id="element-270" data-mp="3poc0codci9gk">
            {children}
          </div>
        </div>
      </div>
    </Fragment>, document.body);
}
export function ModalBody({
  children,
  className = ''
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return <div className={`px-5 lg:px-6 py-4 ${className}`} data-id="element-271" data-mp="2ubu6dhwdi4m3">{children}</div>;
}
export function ModalFooter({
  children,
  className = ''
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return <div className={`px-5 lg:px-6 py-4 border-t border-gray-200 flex items-center justify-end gap-3 ${className}`} data-id="element-272" data-mp="34c30mq6t1fyu">
      
      {children}
    </div>;
}